
import React, { useState, useEffect } from 'react';
import { X, Share2, Search, User, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { useDocuments } from '@/hooks/useDocuments';
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from '@/components/ui/scroll-area';
import { DocumentWithRelations } from '@/types/custom-types';

export interface DocumentShareDialogProps {
  document?: DocumentWithRelations | null;
  documentId?: string; // Alternative to passing a full document
  open: boolean;
  onOpenChange?: (open: boolean) => void;
  onClose?: () => void;
  refetch?: () => void;
  currentPermissions?: any[];
}

export function DocumentShareDialog({ 
  document, 
  documentId,
  open, 
  onOpenChange,
  onClose,
  refetch,
  currentPermissions = []
}: DocumentShareDialogProps) {
  const { 
    getDocumentById, 
    getDocumentPermissions, 
    grantPermission, 
    revokePermission 
  } = useDocuments();
  
  const [userId, setUserId] = useState('');
  const [permissionType, setPermissionType] = useState('view');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Use either passed document or fetch by ID
  const id = document?.id || documentId || '';
  const docQuery = getDocumentById(id);
  const permissionsQuery = getDocumentPermissions(id);
  
  const documentData = document || docQuery.data;
  const permissions = currentPermissions?.length > 0 
    ? currentPermissions 
    : permissionsQuery.data || [];
  const isLoading = !document && docQuery.isLoading;
  
  // Mock users for demo
  const users = [
    { id: 'user-1', first_name: 'John', last_name: 'Doe', email: 'john@example.com' },
    { id: 'user-2', first_name: 'Jane', last_name: 'Smith', email: 'jane@example.com' },
    { id: 'user-3', first_name: 'Robert', last_name: 'Johnson', email: 'robert@example.com' },
  ];
  
  const filteredUsers = users.filter(user => 
    user.first_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.last_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.email.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  const handleAddPermission = async () => {
    if (!userId || !permissionType || !id) return;
    
    await grantPermission({
      document_id: id,
      user_id: userId,
      permission_type: permissionType
    });
    
    setUserId('');
    if (refetch) refetch();
  };
  
  const handleRemovePermission = async (permissionId: string) => {
    await revokePermission(permissionId);
    if (refetch) refetch();
  };
  
  const handleClose = () => {
    if (onClose) onClose();
    if (onOpenChange) onOpenChange(false);
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Share2 className="h-5 w-5" />
            Share Document
          </DialogTitle>
          <DialogDescription>
            {isLoading ? (
              <Skeleton className="h-4 w-full" />
            ) : (
              <>Share "{documentData?.title}" with other users</>
            )}
          </DialogDescription>
        </DialogHeader>
        
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-4 items-center gap-4">
            <div className="col-span-4">
              <label htmlFor="user" className="text-sm font-medium mb-1 block">
                Select User
              </label>
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  id="search-user"
                  placeholder="Search users..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-8"
                />
              </div>
            </div>
            
            <div className="col-span-4">
              <ScrollArea className="h-[150px] border rounded-md p-2">
                <div className="space-y-1">
                  {filteredUsers.map(user => (
                    <div 
                      key={user.id} 
                      className={`flex items-center gap-3 p-2 rounded-md cursor-pointer ${userId === user.id ? 'bg-muted' : 'hover:bg-muted/50'}`}
                      onClick={() => setUserId(user.id)}
                    >
                      <div className="bg-primary/10 h-8 w-8 rounded-full flex items-center justify-center">
                        <User className="h-4 w-4 text-primary" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-medium">{user.first_name} {user.last_name}</p>
                        <p className="text-xs text-muted-foreground">{user.email}</p>
                      </div>
                      {userId === user.id && (
                        <Check className="h-4 w-4 text-primary" />
                      )}
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </div>
            
            <div className="col-span-4 grid grid-cols-4 gap-4">
              <div className="col-span-3">
                <label htmlFor="permission" className="text-sm font-medium mb-1 block">
                  Permission Level
                </label>
                <Select value={permissionType} onValueChange={setPermissionType}>
                  <SelectTrigger id="permission">
                    <SelectValue placeholder="Select permission" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="view">View only</SelectItem>
                    <SelectItem value="comment">Can comment</SelectItem>
                    <SelectItem value="edit">Can edit</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="col-span-1 flex items-end">
                <Button 
                  onClick={handleAddPermission} 
                  className="w-full" 
                  disabled={!userId || !permissionType}
                >
                  Add
                </Button>
              </div>
            </div>
          </div>
          
          {permissionsQuery.isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-12 w-full" />
              <Skeleton className="h-12 w-full" />
            </div>
          ) : permissions && permissions.length > 0 ? (
            <div className="space-y-2">
              <h3 className="text-sm font-medium">Current Permissions</h3>
              <div className="space-y-2">
                {permissions.map(permission => (
                  <div key={permission.id} className="flex items-center justify-between p-2 rounded-md bg-muted/50">
                    <div className="flex items-center gap-2">
                      <div className="bg-primary/10 h-8 w-8 rounded-full flex items-center justify-center">
                        <User className="h-4 w-4 text-primary" />
                      </div>
                      <div>
                        <p className="text-sm font-medium">
                          {permission.user?.first_name} {permission.user?.last_name}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {permission.permission_type} access
                        </p>
                      </div>
                    </div>
                    <Button 
                      variant="ghost" 
                      size="icon"
                      onClick={() => handleRemovePermission(permission.id)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-2 text-sm text-muted-foreground">
              No permissions have been granted yet
            </div>
          )}
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
